import React from 'react';
import { SvgIconProps } from '@mui/material/SvgIcon';
import Visibility from '@mui/icons-material/Visibility';
import Edit from '@mui/icons-material/Edit';
import Add from '@mui/icons-material/Add';
import Block from '@mui/icons-material/Block';
import Settings from '@mui/icons-material/Settings';
import Notifications from '@mui/icons-material/Notifications';
import Person from '@mui/icons-material/Person';
import Sync from '@mui/icons-material/Sync';
import Autorenew from '@mui/icons-material/Autorenew';
import Delete from '@mui/icons-material/Delete';
import Upload from '@mui/icons-material/Upload';
import Search from '@mui/icons-material/Search';
import Close from '@mui/icons-material/Close';
import ArrowDownward from '@mui/icons-material/ArrowDownward';
import ArrowUpward from '@mui/icons-material/ArrowUpward';
import TableRows from '@mui/icons-material/TableRows';
import Apps from '@mui/icons-material/Apps';
import History from '@mui/icons-material/History';
import Description from '@mui/icons-material/Description';
import RestartAlt from '@mui/icons-material/RestartAlt';
import Save from '@mui/icons-material/Save';
import Comment from '@mui/icons-material/Comment';
import Send from '@mui/icons-material/Send';
import QrCode2 from '@mui/icons-material/QrCode2';
import FilterList from '@mui/icons-material/FilterList';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ChevronRight from '@mui/icons-material/ChevronRight';
import ContentCopy from '@mui/icons-material/ContentCopy';
import Share from '@mui/icons-material/Share';
import Lock from '@mui/icons-material/Lock';
import LockOpen from '@mui/icons-material/LockOpen';
import HelpOutline from '@mui/icons-material/HelpOutline';
import CheckCircle from '@mui/icons-material/CheckCircle';
import ErrorOutline from '@mui/icons-material/ErrorOutline';
import PlayCircle from '@mui/icons-material/PlayCircle';
import Inbox from '@mui/icons-material/Inbox';
import Storage from '@mui/icons-material/Storage';
import Storefront from '@mui/icons-material/Storefront';
import LocalOffer from '@mui/icons-material/LocalOffer';
import DirectionsCar from '@mui/icons-material/DirectionsCar';
import AttachMoney from '@mui/icons-material/AttachMoney';
import Public from '@mui/icons-material/Public';
import Calculate from '@mui/icons-material/Calculate';
import ShoppingBag from '@mui/icons-material/ShoppingBag';
import ShoppingCart from '@mui/icons-material/ShoppingCart';
import FormatListNumbered from '@mui/icons-material/FormatListNumbered';
import Payments from '@mui/icons-material/Payments';
import Inventory2 from '@mui/icons-material/Inventory2';
import Article from '@mui/icons-material/Article';
import QrCodeScanner from '@mui/icons-material/QrCodeScanner';
import Check from '@mui/icons-material/Check';
import CloudDownload from '@mui/icons-material/CloudDownload';
import AddCircle from '@mui/icons-material/AddCircle';
import Remove from '@mui/icons-material/Remove';
import Badge from '@mui/icons-material/Badge';
import Logout from '@mui/icons-material/Logout';

type AntIconProps = SvgIconProps & {
  spin?: boolean;
  twoToneColor?: string;
};

function createAntIcon(Icon: React.ComponentType<SvgIconProps>, defaults?: SvgIconProps) {
  const Wrapped = React.forwardRef<SVGSVGElement, AntIconProps>(
    ({ spin, style, sx, twoToneColor, ...props }, ref) => (
      <Icon
        ref={ref}
        {...defaults}
        {...props}
        sx={
          {
            verticalAlign: '-0.125em',
            ...(spin
              ? {
                  animation: 'antdSpin 1s infinite linear',
                  '@keyframes antdSpin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } },
                }
              : {}),
            ...(twoToneColor ? { color: twoToneColor } : {}),
            ...sx,
          } as SvgIconProps['sx']
        }
        style={style as SvgIconProps['style']}
      />
    ),
  );
  Wrapped.displayName = `AntIcon(${Icon.displayName || Icon.name || 'Icon'})`;
  return Wrapped;
}

function createTwoToneIcon(Icon: React.ComponentType<SvgIconProps>, primary = '#1890ff', secondary = '#e6f7ff') {
  return createAntIcon(Icon, { sx: { color: primary, '& path:nth-of-type(2)': { fill: secondary } } });
}

const FallbackIcon = createAntIcon(HelpOutline);

export const EyeOutlined = createAntIcon(Visibility);
export const EditOutlined = createAntIcon(Edit);
export const PlusOutlined = createAntIcon(Add);
export const StopOutlined = createAntIcon(Block);
export const SettingTwoTone = createTwoToneIcon(Settings);
export const BellTwoTone = createTwoToneIcon(Notifications, '#eb2f96', '#fff0f6');
export const UserOutlined = createAntIcon(Person);
export const SyncOutlined = createAntIcon(Sync);
export const Loading3QuartersOutlined = createAntIcon(Autorenew, { sx: { animation: 'antdSpin 1s infinite linear' } });
export const DeleteOutlined = createAntIcon(Delete);
export const UploadOutlined = createAntIcon(Upload);
export const SearchOutlined = createAntIcon(Search);
export const CloseOutlined = createAntIcon(Close);
export const ArrowDownOutlined = createAntIcon(ArrowDownward);
export const ArrowUpOutlined = createAntIcon(ArrowUpward);
export const CaretDownOutlined = createAntIcon(ExpandMore);
export const CaretRightOutlined = createAntIcon(ChevronRight);
export const FileExcelOutlined = createAntIcon(Description);
export const UnorderedListOutlined = createAntIcon(TableRows);
export const AppstoreFilled = createAntIcon(Apps);
export const HistoryOutlined = createAntIcon(History);
export const ReloadOutlined = createAntIcon(RestartAlt);
export const SaveOutlined = createAntIcon(Save);
export const CommentOutlined = createAntIcon(Comment);
export const SendOutlined = createAntIcon(Send);
export const QrcodeOutlined = createAntIcon(QrCode2);
export const FilterOutlined = createAntIcon(FilterList);
export const OrderedListOutlined = createAntIcon(FormatListNumbered);
export const CopyOutlined = createAntIcon(ContentCopy);
export const ShareAltOutlined = createAntIcon(Share);
export const LockOutlined = createAntIcon(Lock);
export const UnlockOutlined = createAntIcon(LockOpen);
export const QuestionCircleOutlined = createAntIcon(HelpOutline);
export const CheckCircleOutlined = createAntIcon(CheckCircle);
export const ExclamationCircleOutlined = createAntIcon(ErrorOutline);
export const PlayCircleOutlined = createAntIcon(PlayCircle);
export const InboxOutlined = createAntIcon(Inbox);
export const CheckOutlined = createAntIcon(Check);
export const DatabaseOutlined = createAntIcon(Storage);
export const ShopOutlined = createAntIcon(Storefront);
export const TagsOutlined = createAntIcon(LocalOffer);
export const CarOutlined = createAntIcon(DirectionsCar);
export const DollarOutlined = createAntIcon(AttachMoney);
export const GlobalOutlined = createAntIcon(Public);
export const CalculatorOutlined = createAntIcon(Calculate);
export const AppstoreOutlined = createAntIcon(Apps);
export const ShoppingOutlined = createAntIcon(ShoppingBag);
export const ShoppingCartOutlined = createAntIcon(ShoppingCart);
export const PayCircleOutlined = createAntIcon(Payments);
export const ContainerOutlined = createAntIcon(Inventory2);
export const FileTextOutlined = createAntIcon(Article);
export const BarcodeOutlined = createAntIcon(QrCodeScanner);

/** Generic fallback when no mapping exists — re-export as AntDesignOutlined */
export const AntDesignOutlined = FallbackIcon;

export const CloudDownloadOutlined = createAntIcon(CloudDownload);
export const PlusCircleOutlined = createAntIcon(AddCircle);
export const MinusOutlined = createAntIcon(Remove);
export const ProfileOutlined = createAntIcon(Badge);
export const LogoutOutlined = createAntIcon(Logout);

export default FallbackIcon;
